import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import queryString from 'query-string';
import useSWRInfinite from 'swr/infinite';
import { useRouter } from 'next/router';
import { parsePaginatedResponse } from 'lib/utils/parser';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { API_ROUTES } from 'lib/api-routes';
import { get } from 'lib/utils/http';
import {
  CommunityTypes,
  CommunityAttributes,
  ExtendedCandidateAttributes,
  CommunityCandidateAttributes,
} from 'lib/models/community';
import { PAGE_ROUTES } from 'lib/page-routes';

export interface ReturnType {
  applications: CommunityAttributes[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMoreRecords: boolean;
  applicationsMetaInfo: { total?: number };
  size: number;
  setSize: (size: number) => void;
  isValidating: boolean;
  mutate: () => void;
  selectedCommunityType: string;
  onChangeCommunityType: (value: string) => void;
  pagesIndex: number;
  setpagesIndex: (pagesIndex: number) => void;
  per_page_count: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const communityParsers: { [key: string]: (item: any) => CommunityAttributes } =
  {
    candidate: (item: ExtendedCandidateAttributes): CommunityAttributes => {
      const { id, ...rest } = item || {};
      return {
        accepted_invitation: false,
        approved: false,
        created_at: '',
        id,
        subscribed_email: false,
        candidate_id: id,
        updated_at: '',
        ...rest,
      };
    },
    student_society_registration: (
      item: CommunityCandidateAttributes
    ): CommunityAttributes => {
      const {
        accepted_invitation,
        approved,
        candidate,
        created_at,
        email,
        first_name,
        id,
        last_name,
        subscribed_email,
        updated_at,
      } = item;
      const {
        id: candidate_id,
        first_name: candidate_first_name,
        last_name: candidate_last_name,
        email: candidate_email,
        ...rest
      } = candidate?.data?.attributes || {};
      return {
        updated_at,
        accepted_invitation,
        approved,
        created_at,
        email,
        first_name: first_name || '',
        id,
        last_name: last_name || '',
        subscribed_email,
        candidate_id,
        ...rest,
      };
    },
  };

const parseCommunityResponse = (
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parsedResponse: any[]
): CommunityAttributes[] => {
  const parserFunction = communityParsers[type];
  if (!parserFunction) {
    console.warn(`No parser found for type: ${type}`);
    return parsedResponse;
  }
  return parsedResponse.map((item) => parserFunction(item));
};

export const useCommunityInfo = (): ReturnType => {
  const { headers } = useRecoilValue(httpHeadersState);
  const router = useRouter();
  const per_page_count = useMemo(() => {
    if (router.pathname === PAGE_ROUTES.COMMUNITY) {
      return 20;
    } else {
      return 10;
    }
  }, [router]);

  const [selectedCommunityType, setSelectedCommunityType] =
    useState<string>('');
  const [pagesIndex, setpagesIndex] = useState(1);
  const onChangeCommunityType = (value: string) => {
    setSelectedCommunityType(value);
  };

  const isSocietyApi = useMemo(() => {
    return [
      CommunityTypes.ATTENDEES,
      CommunityTypes.EVENT_VIEWS,
      CommunityTypes.PROFILE_VIEWS,
    ].includes(selectedCommunityType as CommunityTypes);
  }, [selectedCommunityType]);

  const constructEndpoint = () => {
    return isSocietyApi
      ? `${API_ROUTES.STUDENT_SOCIETY}/${selectedCommunityType}`
      : `${API_ROUTES.ADD_SOCIETY_SUBSCRIBERS}?status=${selectedCommunityType}`;
  };

  const constructQueryString = (pageIndex: number) => {
    const params = {
      per_page: per_page_count,
      with_details: true,
      page:
        router.pathname === PAGE_ROUTES.COMMUNITY ? pageIndex + 1 : pagesIndex,
    };
    return queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });
  };

  const getCommunityKey = (pageIndex: number) => {
    if (selectedCommunityType) {
      return [
        `${constructEndpoint()}${
          isSocietyApi ? '?' : '&'
        }${constructQueryString(pageIndex)}`,
        headers,
      ];
    }
    return null;
  };
  const {
    data: applicationsResponse,
    error: applicationsError,
    size,
    setSize,
    mutate,
    isValidating,
  } =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useSWRInfinite<any>(getCommunityKey, get, {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    });

  const applications = useMemo(() => {
    if (!applicationsResponse) {
      return [];
    } else {
      const parsedResponse = parsePaginatedResponse(applicationsResponse);
      const firstapplicationType = Array.isArray(applicationsResponse)
        ? applicationsResponse[0]?.data?.[0]?.type
        : null;
      return parseCommunityResponse(firstapplicationType, parsedResponse);
    }
  }, [applicationsResponse]);

  const hasMoreRecords = useMemo(() => {
    const firstapplicationList = Array.isArray(applicationsResponse)
      ? applicationsResponse[0]
      : null;
    if (firstapplicationList) {
      return (firstapplicationList?.meta?.total || 0) > size * per_page_count;
    } else {
      return false;
    }
  }, [applicationsResponse, size]);

  const applicationsMetaInfo = useMemo(() => {
    return applicationsResponse?.[0]?.meta || {};
  }, [applicationsResponse]);

  const isLoading = !(applicationsResponse || applicationsError);

  const isLoadingMore =
    isLoading ||
    (size > 0 &&
      applicationsResponse &&
      typeof applicationsResponse[size - 1] === 'undefined');

  return {
    applications,
    pagesIndex,
    setpagesIndex,
    isLoading,
    isLoadingMore,
    hasMoreRecords,
    applicationsMetaInfo,
    size,
    setSize,
    isValidating,
    mutate,
    selectedCommunityType,
    onChangeCommunityType,
    per_page_count,
  };
};
